import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ExperienceShell from './components/ExperienceShell';
import BusinessTypeSelection from './components/BusinessTypeSelection';
import SplashConfig from './components/SplashConfig';
import SiteConfig from './components/SiteConfig';
import BranchSelection from './components/BranchSelection';
import MenuConfig from './components/MenuConfig';
import SpaceCreation from './components/SpaceCreation';
import SectionOrdering from './components/SectionOrdering';
import OrderingConfig from './components/OrderingConfig';
import PlanSelection from './components/PlanSelection';
import AllSetStep from './components/AllSetStep';
import MobileView from './components/MobileView';
import SplashMobileView from './components/SplashMobileView';
import { useExperience } from '../../stores/Experience/Experience';
import { useUsageStore } from '../../stores/Usage';
import { getMaxServiceLevel, hasActiveSubscription } from '../../helpers/planLimits';
import { useAuth } from '../../stores';
import { shallow } from 'zustand/shallow';
import { toast } from 'react-toastify';
import { PlanUpgradeModal } from '../../Components/Common/PlanUpgradeModal';

const THEME_COLOR = '#c59253';

const Experience = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user } = useAuth();
    const userHasActiveSubscription = hasActiveSubscription(user);

    const {
        currentStep,
        setCurrentStep,
        businessType,
        plan,
        siteInfo,
        branding,
        menu,
        spaces,
        splash,
        routing,
        loading,
        showMobilePreview,
        submissionProgress,
        submissionResult,
        error,
        fetchSubmissionResult,
        setBusinessType,
        setPlan,
        setSiteInfo,
        setBranding,
        updateMenu,
        updateSpaces,
        updateSplash,
        updateRouting,
        submitExperience,
        toggleMobilePreview,
        experienceMode,
        setExperienceMode,
        selectedSiteId,
        setSelectedSiteId
    } = useExperience(state => ({
        currentStep: state.currentStep,
        setCurrentStep: state.setCurrentStep,
        businessType: state.businessType,
        plan: state.plan,
        siteInfo: state.siteInfo,
        branding: state.branding,
        menu: state.menu,
        spaces: state.spaces,
        splash: state.splash,
        routing: state.routing,
        loading: state.loading,
        showMobilePreview: state.showMobilePreview,
        submissionProgress: state.submissionProgress,
        submissionResult: state.submissionResult,
        error: state.error,
        fetchSubmissionResult: state.fetchSubmissionResult,
        setBusinessType: state.setBusinessType,
        setPlan: state.setPlan,
        setSiteInfo: state.setSiteInfo,
        setBranding: state.setBranding,
        updateMenu: state.updateMenu,
        updateSpaces: state.updateSpaces,
        updateSplash: state.updateSplash,
        updateRouting: state.updateRouting,
        submitExperience: state.submitExperience,
        toggleMobilePreview: state.toggleMobilePreview,
        experienceMode: state.experienceMode,
        setExperienceMode: state.setExperienceMode,
        selectedSiteId: state.selectedSiteId,
        setSelectedSiteId: state.setSelectedSiteId
    }), shallow);
    const terminology = useExperience.getState().getTerminology();
    // Derived state for compatibility with children
    const state = {
        plan,
        businessType,
        splashData: splash, // Mapping splash -> splashData
        siteInfo: siteInfo || {},
        branding: branding || {},
        sections: menu.sections, // Mapping menu.sections -> sections
        items: [], // Deprecated in favor of sections? Or flattened items. Assuming sections.items
        spaces,
        orderingConfig: routing || { enabled: true, mode: 'all', enabledSpaces: [] }
    };

    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const { getLimitInfo, fetchUsage } = useUsageStore();

    // Wrapper for updateState to maintain API compatibility with children
    const updateState = (updates) => {
        // We need to map the flat updates object back to specific store actions
        if ('plan' in updates) setPlan(updates.plan);
        if ('businessType' in updates) setBusinessType(updates.businessType);
        if ('selectedSiteId' in updates) setSelectedSiteId(updates.selectedSiteId);

        if ('siteInfo' in updates) {
            // Handle partial updates to siteInfo
            setSiteInfo({ ...siteInfo, ...updates.siteInfo });
        }

        if ('splashData' in updates) updateSplash(updates.splashData);

        if ('sections' in updates) {
            updateMenu({ sections: updates.sections });
        }

        if ('spaces' in updates) updateSpaces(updates.spaces);

        if ('orderingConfig' in updates) updateRouting(updates.orderingConfig);
        if ('experienceMode' in updates) {
            setExperienceMode(updates.experienceMode);
            // If switching modes, we might want to reset current step?
            // Usually switching to space-first happens after menu-first is done or as an alternative.
            // Leaving currentStep as is but the filter will change the view.
        }
    };

    const isFree = plan === 'free';

    // Define all steps
    const allSteps = [
        // {
        //     name: 'Plan Selection',
        //     component: PlanSelection,
        //     journey: 'both',
        //     skip: userHasActiveSubscription
        // },
        {
            name: t('Business Type'),
            component: BusinessTypeSelection,
            journey: 'both',
            skip: false
        },
        // {
        //     name: t('Branch Selection'),
        //     component: BranchSelection,
        //     journey: 'both',
        //     skip: false
        // },
        {
            name: t('Site Information'),
            component: SiteConfig,
            journey: 'both',
            skip: () => !!selectedSiteId
        },

        // Journey A: Menu-First (Basic)
        {
            name: t('Menu Builder'),
            component: MenuConfig,
            journey: 'menu-first',
            skip: false
        },
        // {
        //     name: 'Splash Configuration',
        //     component: SplashConfig,
        //     journey: 'menu-first',
        //     skip: () => businessType !== 'restaurant'
        // },

        // Journey B: Space-First (Advanced / Legacy)
        // Note: Space-First in this wizard is now mostly legacy as we have a dedicated Space Manager wizard
        {
            name: `${terminology.space} ${t('Creation')}`,
            component: SpaceCreation,
            journey: 'space-first',
            skip: false
        },
        {
            name: t('Menu Builder'),
            component: MenuConfig,
            journey: 'space-first',
            skip: false
        },
        {
            name: t('Menu Sequencing'),
            component: SectionOrdering,
            journey: 'space-first',
            skip: false
        },
        {
            name: t('Ordering Configuration'),
            component: OrderingConfig,
            journey: 'space-first',
            skip: () => getMaxServiceLevel(useUsageStore.getState().usage) < 3
        },
    ];

    // Filter based on Journey Mode
    const steps = allSteps.filter(step => {
        // Step must match current journey mode or be 'both'
        if (step.journey !== 'both' && step.journey !== experienceMode) return false;

        if (typeof step.skip === 'function') {
            return !step.skip();
        }
        return !step.skip;
    });

    // Handle step boundary
    // Note: currentStep in store is 1-indexed, but steps array is 0-indexed logic here
    // Let's align on 0-indexed for the view logic
    const stepIndex = currentStep - 1;
    const CurrentStepComponent = steps[stepIndex]?.component;


    useEffect(() => {
        fetchUsage();
    }, []);

    // Recovery logic for refresh on last step
    useEffect(() => {
        const activeStepsCount = allSteps.filter(step => {
            if (typeof step.skip === 'function') return !step.skip();
            return !step.skip;
        }).length + 1; // +1 for AllSet

        if (currentStep === activeStepsCount && submissionResult?.site_id && !submissionResult?.space_info) {
            console.log("Detecting refresh on final step, recovering data...");
            fetchSubmissionResult(submissionResult.site_id);
        }
    }, [currentStep, submissionResult, fetchSubmissionResult]);

    const handleNext = async () => {
        // Validation Logic
        const currentStepName = activeSteps[stepIndex]?.name;

        if (currentStepName === 'Business Type') {
            const siteLimit = getLimitInfo('sites');
            if (siteLimit.isReached) {
                toast.error(t(`You have reached your site limit (${siteLimit.limit}/${siteLimit.limit}). Please upgrade to add more sites.`));
                setShowUpgradeModal(true);
                return;
            }
        }

        if (currentStepName === 'Site Information') {
            if (!state.siteInfo?.businessName) {
                toast.warning(`${t('Please enter a')} ${useExperience.getState().getTerminology().space} ${t('name')}`);
                return;
            }
        }

        if (currentStepName === 'Menu Builder') {
            if (!state.sections || state.sections.length === 0) {
                toast.warning(t('Please add at least one section'));
                return;
            }

            const totalItems = state.sections.reduce((acc, section) => acc + (section.items?.length || 0), 0);
            if (totalItems === 0) {
                toast.warning(t('Please add at least one item'));
                return;
            }
        }

        if (currentStepName === `${terminology.space} ${t('Creation')}`) {
            if (!state.spaces || state.spaces.length === 0) {
                toast.warning(`${t('Please create at least one')} ${terminology.space}`);
                return;
            }
        }

        if (stepIndex < steps.length - 1) {
            setCurrentStep(currentStep + 1);
        } else {
            // Final step - trigger submission
            try {
                const result = await submitExperience();
                // Move to AllSetStep (appended at end)
                setCurrentStep(currentStep + 1);
            } catch (error) {
                console.error("Submission failed", error);
                toast.error(t("Submission failed: ") + (error.message || t("Unknown error")));
            }
        }
    };

    // Fix: Re-add AllSet to steps
    const stepsWithFinal = [
        ...steps,
        { name: t('All Set'), component: AllSetStep, skip: false }
    ];

    // Re-calculate stepIndex based on potentially filtered steps
    // This logic is tricky if steps are dynamic.
    // Better to keep steps static or handle index carefully.
    // The previous code filtered `allSteps`.

    const activeSteps = allSteps.filter(step => {
        if (step.journey !== 'both' && step.journey !== experienceMode) return false;
        if (typeof step.skip === 'function') {
            return !step.skip();
        }
        return !step.skip;
    });

    // Append AllSetStep
    activeSteps.push({ name: t('All Set'), component: AllSetStep, skip: false });

    // Safety check
    if (stepIndex >= activeSteps.length) {
        // setCurrentStep(1); // Reset?
    }

    const ActiveComponent = activeSteps[stepIndex]?.component;

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleSkip = () => {
        // Move to next
        setCurrentStep(currentStep + 1);
    };

    return (
        <div className="min-vh-100 bg-light position-relative">
            {loading && (
                <div
                    className="position-fixed top-0 start-0 w-100 h-100 bg-white bg-opacity-75 d-flex flex-column align-items-center justify-content-center"
                    style={{ zIndex: 9999 }}
                >
                    <div className="spinner-border text-warning mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                        <span className="visually-hidden">{t('Loading...')}</span>
                    </div>
                    <h5 className="fw-bold text-dark">{t('Setting up your experience...')}</h5>
                    <p className="text-muted">{submissionProgress || t('Please wait...')}</p>
                    {error && <p className="text-danger mt-2">{error}</p>}
                </div>
            )}

            {ActiveComponent === AllSetStep ? (
                // Render AllSetStep without Shell usually? Or with Shell?
                // Previous code: AllSetStep was just another component in the shell.
                // Let's keep it consistent.
                <ExperienceShell
                    currentStep={currentStep}
                    totalSteps={activeSteps.length}
                    plan={plan}
                    onBack={undefined} // No back from final step?
                    onNext={undefined} // Handled by component
                    onSkip={undefined}
                    hideActions={true} // Hide shell actions for final step
                    preview={null}
                >
                    <AllSetStep state={state} result={submissionResult} />
                </ExperienceShell>
            ) : (
                <ExperienceShell
                    currentStep={currentStep}
                    totalSteps={activeSteps.length}
                    plan={plan}
                    onBack={currentStep > 1 ? handleBack : undefined}
                    onNext={handleNext}
                    onSkip={undefined}
                    nextLabel={stepIndex === activeSteps.length - 2 ? t('Complete') : t('Continue')}
                    preview={
                        activeSteps[stepIndex]?.name === 'Splash Configuration' && splash.enabled !== false
                            ? <SplashMobileView state={state} />
                            : <MobileView state={state} />
                    }
                >
                    {ActiveComponent && (
                        <ActiveComponent
                            state={state}
                            updateState={updateState}
                            onNext={handleNext}
                            onBack={handleBack}
                        />
                    )}
                </ExperienceShell>
            )}

            <PlanUpgradeModal
                isOpen={showUpgradeModal}
                toggle={() => setShowUpgradeModal(!showUpgradeModal)}
            />
        </div>
    );
};

export default Experience;
